import type { StocktakeCountResource } from '@repo/api-client';

import { formatLevel } from './stock-levels';

/**
 * What the server answered about one counted item, written the way it has to be
 * read.
 *
 * This module exists for the same reason `stock-levels.ts` does: three facts here
 * are easy to lose on the way to a cell, and each of them loses something
 * different.
 *
 * - **A count that agreed is a completed count, not a no-op** (FR-539c).
 *   `variance: 0` with `movementId: null` means somebody walked the shelf, the
 *   records were right, and that is now on the record. Rendering it as "nothing
 *   happened" — greying the row, dropping it, saying "no change" — throws away
 *   the only evidence that the shelf was ever counted, which is the reason
 *   anybody counted it.
 * - **`computedQuantity` may be negative** (CL-501). It is what the system
 *   believed at the moment of the count, and a level is never clamped, so `−2` is
 *   a true figure meaning the records owe the shelf two. Shown as it stands, by
 *   the same `formatLevel` the levels table uses.
 * - **The variance is the server's arithmetic and appears nowhere before the
 *   answer.** Nothing in this app subtracts one figure from the other; it reads
 *   `variance` off the response. A screen that did the sum itself would be a
 *   second opinion about a number that has one, and the point of the route
 *   (CL-503) is that the sum is not the person's to do.
 */

/** Which of the three things one line came back saying. */
export type CountOutcome = 'agreed' | 'short' | 'over';

/**
 * What this line found.
 *
 * Read off `variance` and not recomputed from the two quantities: the server
 * derived it, and it is the field the contract describes.
 */
export function outcomeOf(count: StocktakeCountResource): CountOutcome {
  if (count.variance === 0) return 'agreed';

  return count.variance < 0 ? 'short' : 'over';
}

/**
 * Whether this count wrote a compensating movement.
 *
 * `movementId` and not `variance !== 0`, although the two agree: this is the
 * question "did the level move", the id is the answer, and reading the id is what
 * keeps the badge honest if the server ever writes one for a reason this screen
 * does not know about.
 */
export const wroteMovement = (count: StocktakeCountResource): boolean => count.movementId !== null;

/**
 * A variance as somebody reads it: `+3`, `−2`, `0`.
 *
 * Built on `formatLevel` so the true minus sign, the tabular width and the Indian
 * grouping are decided in one place. The **plus is explicit**, which `formatLevel`
 * deliberately does not do for a level: a level is a quantity and a variance is a
 * direction, and an unsigned `3` in a column that also holds `−2` reads as three
 * of something rather than three more than expected.
 */
export function formatVariance(variance: number): string {
  return variance > 0 ? `+${formatLevel(variance)}` : formatLevel(variance);
}

/**
 * The short form for a badge: what this line found, in two or three words.
 *
 * Says "fewer" and "more" rather than "short" and "over", because the sentence
 * they are shortening is "fewer on the shelf than the records said" — and because
 * "over" in a stock system reads as an overage that somebody has to book
 * somewhere, which is the movement the server has already written.
 */
export function outcomeSays(count: StocktakeCountResource): string {
  const outcome = outcomeOf(count);

  if (outcome === 'agreed') return 'Agreed';

  const magnitude = formatLevel(Math.abs(count.variance));

  return outcome === 'short' ? `${magnitude} fewer` : `${magnitude} more`;
}

/** How many of these counts agreed with the records. */
export const countsAgreed = (counts: readonly StocktakeCountResource[]): number =>
  counts.filter((count) => outcomeOf(count) === 'agreed').length;

/** How many of them wrote a movement to settle the difference. */
export const countsSettled = (counts: readonly StocktakeCountResource[]): number =>
  counts.filter(wroteMovement).length;
